import { lazy, Suspense, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, RotateCcw, Phone, RefreshCw, Truck, AlertCircle, Users, MessageSquare, Mic, Settings } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AdminProvider, useAdminContext } from './hooks/useAdminContext';
import { AdminTabSkeleton } from './components/AdminTabSkeleton';
import type { TabId } from './types';

// Lazy load all tabs
const UsersTab = lazy(() => import('./tabs/UsersTab').then(m => ({ default: m.UsersTab })));
const OrdersTab = lazy(() => import('./tabs/OrdersTab').then(m => ({ default: m.OrdersTab })));
const ReturnsTab = lazy(() => import('./tabs/ReturnsTab').then(m => ({ default: m.ReturnsTab })));
const PickupsTab = lazy(() => import('./tabs/PickupsTab').then(m => ({ default: m.PickupsTab })));
const CallbacksTab = lazy(() => import('./tabs/CallbacksTab').then(m => ({ default: m.CallbacksTab })));
const TextChatsTab = lazy(() => import('./tabs/TextChatsTab').then(m => ({ default: m.TextChatsTab })));
const VoiceChatsTab = lazy(() => import('./tabs/VoiceChatsTab').then(m => ({ default: m.VoiceChatsTab })));
const PhoneCallsTab = lazy(() => import('./tabs/PhoneCallsTab').then(m => ({ default: m.PhoneCallsTab })));
const SettingsTab = lazy(() => import('./tabs/SettingsTab').then(m => ({ default: m.SettingsTab })));

interface TabConfig {
  id: TabId;
  labelKey: string;
  icon: typeof Users;
}

const TABS: TabConfig[] = [
  { id: 'users', labelKey: 'admin.users', icon: Users },
  { id: 'orders', labelKey: 'admin.orders', icon: Package },
  { id: 'returns', labelKey: 'admin.returns', icon: RotateCcw },
  { id: 'pickups', labelKey: 'admin.pickups', icon: Truck },
  { id: 'callbacks', labelKey: 'admin.callbacks', icon: Phone },
  { id: 'text_chats', labelKey: 'admin.text_chats', icon: MessageSquare },
  { id: 'web_voice', labelKey: 'admin.voice_chats', icon: Mic },
  { id: 'phone_calls', labelKey: 'admin.phone', icon: Phone },
  { id: 'settings', labelKey: 'admin.settings', icon: Settings },
];

function AdminPageContent() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useLanguage();
  const { isAdmin, loading, loadData } = useAdminContext();
  const [activeTab, setActiveTab] = useState<TabId>('orders');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-xl font-bold mb-2">Niet ingelogd</h1>
          <p className="text-muted-foreground mb-4">Log in om het dashboard te bekijken.</p>
          <button onClick={() => navigate('/login')} className="px-4 py-2 bg-primary text-white rounded-lg">
            Inloggen
          </button>
        </div>
      </div>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'users': return <UsersTab />;
      case 'orders': return <OrdersTab />;
      case 'returns': return <ReturnsTab />;
      case 'pickups': return <PickupsTab />;
      case 'callbacks': return <CallbacksTab />;
      case 'text_chats': return <TextChatsTab />;
      case 'web_voice': return <VoiceChatsTab />;
      case 'phone_calls': return <PhoneCallsTab />;
      case 'settings': return <SettingsTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8 pb-32">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">{isAdmin ? t('admin.title') : t('admin.my_overview')}</h1>
          <button
            onClick={() => loadData()}
            className="ml-auto p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card transition-colors"
            title={t('admin.refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-primary/10 overflow-x-auto pb-px -mx-4 px-4 sm:mx-0 sm:px-0">
          {TABS.map(tab => {
            // Settings tab is admin-only
            if (tab.id === 'settings' && !isAdmin) return null;
            // Non-admins only see profile tab for users
            if (tab.id === 'users' && !isAdmin) {
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 sm:px-4 py-2 font-medium transition-colors whitespace-nowrap text-sm ${
                    activeTab === tab.id
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t('admin.my_profile')}
                </button>
              );
            }
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-4 py-2 font-medium transition-colors whitespace-nowrap text-sm ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="flex items-center gap-1">
                  {(tab.id === 'text_chats' || tab.id === 'web_voice' || tab.id === 'phone_calls') && (
                    <tab.icon className="w-3 h-3" />
                  )}
                  {t(tab.labelKey)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <Suspense fallback={<AdminTabSkeleton />}>
          {renderTab()}
        </Suspense>
      </div>
    </div>
  );
}

export default function AdminPage() {
  return (
    <AdminProvider>
      <AdminPageContent />
    </AdminProvider>
  );
}
